import { formatRecordLabelTemplate } from "../recordLabelTemplate";
/**
 * CanvasRenderer - Shared grid-based canvas renderer
 * Used by both CanvasBuilder (edit mode) and CanvasApp (runtime mode)
 * Ensures WYSIWYG consistency between editor and runtime.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Input,
  Textarea,
  Select,
  Option,
  Checkbox,
  FormControl,
  FormLabel,
  FormHelperText,
  Sheet,
  Divider,
  Card,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  Radio,
  RadioGroup,
} from "@mui/joy";
import * as MuiIcons from "@mui/icons-material";
import { CanvasFileField } from "./elements/CanvasFileField";
import ViewGridErrorBoundary from "../viewGrid/ViewGridErrorBoundary";
import { ActionButtonElement } from "./elements/ActionButtonElement";
import { CanvasViewGridPlaceholder } from "./elements/CanvasViewGridPlaceholder";
import { useCanvasRuntime } from "./CanvasRuntimeContext";
import { CanvasAggregateWidget } from "./elements/CanvasAggregateWidget";
import { tabPresentation } from "./containers/tabPresentation";
import { CanvasAutoHeightContext, compactContentRows } from "./contentHeight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type {
  CanvasGridConfig,
  CanvasElementProps,
  ContainerElementConfig,
  VisibilityRules,
  VisibilityCondition,
} from "../../types";
import { effectiveColumns } from "../../responsive/breakpoints";
import type { BreakpointKind } from "../../responsive/breakpoints";
import {
  mapScopeWithColumns,
  DEFAULT_CHILD_GRID_COLUMNS,
} from "../../responsive/responsiveLayout";
import type {
  ResponsiveItem,
  MappedItem,
} from "../../responsive/responsiveLayout";
import { useCanvasBreakpoint } from "../../responsive/useCanvasBreakpoint";
import StatusChip from "./elements/StatusChip";
import InitialsAvatar from "./elements/InitialsAvatar";
import {
  resolveStyleOverrides,
  sanitizeStatusColors,
  type ResolvedStyleOverrides,
} from "./canvasStyleOverrides";
import type { EffectiveElementAccess } from "../../views/runtimeElementAccess";

/**
 * Helper to get MUI Icon component by name.
 */
function getMuiIcon(iconName: string): React.ElementType | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const icons = MuiIcons as Record<string, any>;
  return icons[iconName] || icons[`${iconName}Outlined`] || null;
}

/**
 * Helper to map heading level to MUI Typography level.
 */
function getHeadingLevel(
  level: number,
): "h1" | "h2" | "h3" | "h4" | "title-lg" | "title-md" {
  switch (level) {
    case 1:
      return "h1";
    case 2:
      return "h2";
    case 3:
      return "h3";
    case 4:
      return "h4";
    case 5:
      return "title-lg";
    case 6:
    default:
      return "title-md";
  }
}

/**
 * True for an http(s) or scheme-less relative URL. Rejects every other scheme
 * (javascript:, data:, ftp: ...) and protocol-relative `//host` URLs.
 */
function isSafeImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  // Refuse control characters and backslashes, which browsers normalise into other URLs.
  if (Array.from(value).some((c) => c.charCodeAt(0) <= 0x1f || c === "\\"))
    return false;
  if (value.startsWith("//")) return false;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value);
  return scheme ? /^https?$/i.test(scheme[1]) : true;
}

/**
 * Renders text/static elements with variant support.
 */
function renderTextElement(props: CanvasElementProps): React.JSX.Element {
  const textVariant = props.textVariant || "text";

  // Common text styles
  const textStyles = {
    color: props.textColor || undefined,
    textAlign: props.textAlign || undefined,
    fontWeight: props.fontWeight || undefined,
  };

  switch (textVariant) {
    case "heading": {
      const level = props.level || 2;
      return (
        <Typography level={getHeadingLevel(level)} sx={textStyles}>
          {props.label || ""}
        </Typography>
      );
    }

    case "html": {
      return (
        <Box
          sx={{ ...textStyles }}
          dangerouslySetInnerHTML={{
            __html: props.htmlContent || props.label || "",
          }}
        />
      );
    }

    case "icon": {
      const IconComponent = props.iconName ? getMuiIcon(props.iconName) : null;
      if (!IconComponent) {
        return (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            {props.iconName
              ? `Icon not found: ${props.iconName}`
              : "No icon specified"}
          </Typography>
        );
      }
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: props.textAlign || "center",
            ...textStyles,
          }}
        >
          <IconComponent
            sx={{
              fontSize:
                props.size === "lg" ? 48 : props.size === "sm" ? 24 : 32,
              color: props.textColor || "inherit",
            }}
          />
        </Box>
      );
    }

    case "spacer": {
      const height = props.spacerHeight || 24;
      return <Box sx={{ height, minHeight: height }} />;
    }

    case "image": {
      const url = props.imageUrl || props.image_url;
      if (typeof url !== "string" || !url || !isSafeImageUrl(url)) {
        return (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            Image unavailable
          </Typography>
        );
      }
      return (
        <Box
          component="img"
          src={url.trim()}
          alt={props.imageAlt ?? props.image_alt ?? ""}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: props.imageFit ?? props.image_fit ?? "contain",
          }}
        />
      );
    }

    case "divider": {
      return (
        <Divider
          sx={{
            my: 1,
            borderStyle: props.dividerStyle ?? props.divider_style,
          }}
        />
      );
    }

    case "text":
    default: {
      // Default text rendering (backward compatible)
      return (
        <Typography
          level={
            props.size === "lg"
              ? "h4"
              : props.size === "sm"
                ? "body-sm"
                : "body-md"
          }
          sx={textStyles}
        >
          {props.label || ""}
        </Typography>
      );
    }
  }
}

/**
 * Evaluates visibility rules against current form data.
 * Returns true if the element should be visible, false otherwise.
 *
 * @param rules - The visibility rules to evaluate
 * @param formData - The current form data (keyed by field path)
 * @returns boolean - Whether the element should be visible
 */
export function evaluateVisibilityRules(
  rules: VisibilityRules | undefined,
  formData: Record<string, unknown>,
): boolean {
  // No rules = always visible
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return true;
  }

  const evaluateCondition = (cond: VisibilityCondition): boolean => {
    const value = formData[cond.field];

    switch (cond.operator) {
      case "eq":
        return value === cond.value;
      case "ne":
        return value !== cond.value;
      case "gt":
        return Number(value) > Number(cond.value);
      case "lt":
        return Number(value) < Number(cond.value);
      case "gte":
        return Number(value) >= Number(cond.value);
      case "lte":
        return Number(value) <= Number(cond.value);
      case "in":
        if (Array.isArray(cond.value)) {
          return cond.value.includes(value);
        }
        return false;
      case "not_in":
        if (Array.isArray(cond.value)) {
          return !cond.value.includes(value);
        }
        return true;
      case "is_empty":
        return (
          !value || value === "" || (Array.isArray(value) && value.length === 0)
        );
      case "is_not_empty":
        return (
          !!value &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
        );
      default:
        return true;
    }
  };

  // Apply logic (AND or OR)
  if (rules.logic === "and") {
    return rules.conditions.every(evaluateCondition);
  } else {
    return rules.conditions.some(evaluateCondition);
  }
}

/**
 * Normalized element structure for rendering.
 * Both CanvasBuilder and CanvasApp transform their elements to this format.
 */
export interface RenderableElement {
  id: string;
  type: string;
  effectiveAccess?: EffectiveElementAccess;
  variant?: string;
  props: CanvasElementProps;
  gridColumn: number;
  gridRow: number;
  columnSpan: number;
  rowSpan: number;
  children?: RenderableElement[];
  data_binding?: {
    data_source_id: string;
    field_path: string;
  };
  // Nesting support
  parentElementId?: string;
  parentPanelId?: string;
  displayOrder?: number;
  /** Saved display visibility; editors can still see hidden elements. */
  isVisible?: boolean;
  // Visibility rules
  visibilityRules?: VisibilityRules;
  /** Stored `style_overrides`; only the whitelisted keys are rendered (canvasStyleOverrides.ts). */
  styleOverrides?: Record<string, unknown>;
}

/**
 * Props for form field state management in runtime mode.
 */
export interface FieldState {
  value?: unknown;
  error?: string;
  touched?: boolean;
  onChange?: (value: unknown) => void;
  onBlur?: () => void;
}

const ElementOverrideContext = createContext<
  ((element: RenderableElement) => React.ReactNode | undefined) | undefined
>(undefined);

interface CanvasRendererProps {
  /** Offline hosts may replace leaves that require live data or authentication. */
  renderElementOverride?: (
    element: RenderableElement,
  ) => React.ReactNode | undefined;
  /** Elements to render on the canvas */
  elements: RenderableElement[];
  /** Grid configuration */
  config: CanvasGridConfig;
  /** Mode: 'edit' for builder preview, 'runtime' for actual form */
  mode: "edit" | "runtime";
  /** Hide saved-hidden elements in an edit-mode runtime preview. */
  respectSavedVisibility?: boolean;
  /** Field state map for runtime mode (keyed by field_path) */
  fieldState?: Record<string, FieldState>;
  /** Optional override for rendering embedded canvas elements in runtime */
  renderEmbeddedCanvas?: (element: RenderableElement) => React.ReactNode;
  /** Optional override for rendering authenticated view-grid elements in runtime */
  renderViewGrid?: (element: RenderableElement) => React.ReactNode;
  renderTimeline?: (element: RenderableElement) => React.ReactNode;
  /** Draws KANBAN and CALENDAR widgets (they need the canvas data sources and write context). */
  renderDataWidget?: (element: RenderableElement) => React.ReactNode;
  /** Canvas title */
  title?: string;
  /** Canvas description */
  description?: string;
  /** Additional content to render after elements (e.g., submit button) */
  footer?: React.ReactNode;
  /**
   * When true, place root-level elements for the current viewport breakpoint
   * using the deterministic responsive mapping (spec 7.2): desktop is the
   * authored layout, tablet re-packs into 8 columns, phone stacks single-column.
   * Defaults to false, in which case rendering is byte-identical to the authored
   * (desktop) layout. Runtime hosts (CanvasApp, PublicCanvasViewer) pass true;
   * the builder preview leaves it false so authoring stays WYSIWYG at desktop.
   */
  responsive?: boolean;
  /**
   * When set, the responsive mapping is driven by this breakpoint instead of the
   * live viewport width. The builder preview uses it to show the tablet/phone
   * layout inside a constrained preview pane (whose own width does not match the
   * simulated device). Passing it implies responsive mapping is active even when
   * `responsive` is false, so a caller can force a breakpoint without also opting
   * the live-viewport path in. Leave undefined for viewport-driven behaviour.
   */
  forcedBreakpoint?: BreakpointKind;
  /**
   * Called with a TABS container's element id and the id of the panel it shows,
   * on first render and whenever the shown panel changes. Lets the host scope
   * canvas-level controls to the active tab.
   */
  onActivePanelChange?: (containerId: string, panelId: string) => void;
}

/**
 * The effective child-column count of a container's nested scope at a
 * breakpoint (the `C` its children are packed against), used both to map the
 * children and to size the nested CSS grid `repeat(Cc, 1fr)`:
 *  - desktop: the container's authored child columns (default 12) -- identity,
 *    so a desktop / responsive-off canvas keeps the hardcoded 12-column nest.
 *  - phone: 1 (children stack single-column).
 *  - tablet: the container's own MAPPED span, which for in-bounds authored
 *    widths equals spec-7.2's `Cc = nestedChildColumns(authoredW, C, G)`.
 */
function childScopeColumns(
  breakpoint: BreakpointKind,
  mappedContainerWidth: number,
  authoredChildColumns: number,
): number {
  if (breakpoint === "phone") return 1;
  if (breakpoint === "desktop") return authoredChildColumns;
  return Math.max(1, mappedContainerWidth);
}

/** A container element's authored child-column count (its nested grid's G). */
function authoredChildColumnsOf(el: RenderableElement): number {
  // Read the product's container discriminator. A container stores its
  // ContainerElementConfig flat on `props`; the product's container renderers
  // (TabsRenderer, SectionRenderer, ColumnsRenderer) all read
  // `config.internalGrid.columns`. Use the same path so an authored nested
  // column count (for example 6) is honored, not silently treated as the
  // 12-column default.
  const config = el.props as unknown as ContainerElementConfig;
  const declared = config.internalGrid?.columns;
  return typeof declared === "number" && declared > 0
    ? declared
    : DEFAULT_CHILD_GRID_COLUMNS;
}

/**
 * Build a flat `id -> MappedItem` geometry map for every element at the target
 * breakpoint, respecting panel scoping (a TABS container's children are mapped
 * per panel, never as one interleaved scope). Each scope is mapped with its own
 * effective columns: the root scope uses the breakpoint's standard columns; a
 * nested scope uses `childScopeColumns` derived from its container's mapped
 * span. The returned map carries each container's `childColumns` so the renderer
 * can size the nested grid. Returns null when mapping is inactive, in which case
 * callers keep authored positions (byte-identical to before this change).
 */
export function buildGeometryMap(
  elements: RenderableElement[],
  breakpoint: BreakpointKind,
  rootColumns: number,
  authoredRootColumns: number,
  active: boolean,
  compactDesktop = false,
): Map<string, MappedItem> | null {
  if (!active) return null;
  const map = new Map<string, MappedItem>();

  const mapOneScope = (
    scopeEls: RenderableElement[],
    scopeColumns: number,
    scopeAuthoredColumns: number,
  ) => {
    const items: ResponsiveItem[] = scopeEls.map((el, idx) => ({
      id: el.id,
      col: el.gridColumn,
      row: el.gridRow,
      w: el.columnSpan,
      h: el.rowSpan,
      order: el.displayOrder ?? idx,
    }));
    const mapped = mapScopeWithColumns(
      items,
      breakpoint,
      scopeColumns,
      scopeAuthoredColumns,
      compactDesktop,
    );
    const byId = new Map(mapped.map((m) => [m.id, m]));

    for (const el of scopeEls) {
      const m = byId.get(el.id);
      if (!m) continue;
      map.set(el.id, m);

      const kids = elements.filter((c) => c.parentElementId === el.id);
      if (kids.length === 0) continue;

      const authoredChildCols = authoredChildColumnsOf(el);
      const childCols = childScopeColumns(breakpoint, m.w, authoredChildCols);
      m.childColumns = childCols;

      // Map each panel's children as an independent scope (SECTION/CARD have a
      // single unnamed panel; TABS has one per panel id).
      const panelIds = Array.from(
        new Set(kids.map((c) => c.parentPanelId ?? "")),
      );
      for (const panelId of panelIds) {
        const panelKids = kids.filter(
          (c) => (c.parentPanelId ?? "") === panelId,
        );
        mapOneScope(panelKids, childCols, authoredChildCols);
      }
    }
  };

  const roots = elements.filter((el) => !el.parentElementId);
  mapOneScope(roots, rootColumns, authoredRootColumns);
  return map;
}

/** Lets a tab control tell the host which panel it shows. */
const ActivePanelContext = createContext<
  ((containerId: string, panelId: string) => void) | undefined
>(undefined);

/**
 * TabsContainer - Renders a tabbed container with panel switching.
 */
function TabsContainer({
  containerId,
  panels,
  defaultPanelId,
  tabStyle,
  renderChildren,
}: {
  containerId: string;
  title?: string;
  panels: Array<{ id: string; label: string; icon?: string }>;
  /** The panel to open on; the first panel when absent or not one of `panels`. */
  defaultPanelId?: string;
  tabStyle?: string;
  renderChildren: (panelId: string) => React.ReactNode;
}) {
  const onActivePanelChange = useContext(ActivePanelContext);
  const presentation = tabPresentation(tabStyle);
  const autoHeight = useContext(CanvasAutoHeightContext);
  const [activeTab, setActiveTab] = useState<string>(
    panels.find((panel) => panel.id === defaultPanelId)?.id ||
      panels[0]?.id ||
      "",
  );

  // Tell the host which panel is shown, so it can scope canvas-level controls
  // (such as the submit footer) to the tab they belong to.
  useEffect(() => {
    if (activeTab) onActivePanelChange?.(containerId, activeTab);
  }, [activeTab, containerId, onActivePanelChange]);

  return (
    <Box
      sx={{
        height: autoHeight ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "md",
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value as string)}
        sx={presentation?.tabs ?? { bgcolor: "background.level1" }}
      >
        <TabList sx={presentation?.list}>
          {panels.map((panel) => {
            const PanelIcon = panel.icon ? getMuiIcon(panel.icon) : null;
            return (
              <Tab
                key={panel.id}
                value={panel.id}
                indicatorPlacement={presentation ? "bottom" : undefined}
                sx={{ gap: 0.75, ...presentation?.tab }}
              >
                {PanelIcon ? <PanelIcon fontSize="small" /> : null}
                {panel.label}
              </Tab>
            );
          })}
        </TabList>
        {panels.map((panel) => (
          <TabPanel
            key={panel.id}
            value={panel.id}
            sx={{ p: 2, flexGrow: 1, overflow: "auto" }}
          >
            {renderChildren(panel.id)}
          </TabPanel>
        ))}
      </Tabs>
    </Box>
  );
}

/**
 * CanvasRenderer - Renders elements on a CSS grid.
 * Provides consistent layout between edit preview and runtime execution.
 */
export function CanvasRenderer({
  renderElementOverride,
  onActivePanelChange,
  ...props
}: CanvasRendererProps) {
  return (
    <ElementOverrideContext.Provider value={renderElementOverride}>
      <ActivePanelContext.Provider value={onActivePanelChange}>
        <CanvasRendererContent {...props} />
      </ActivePanelContext.Provider>
    </ElementOverrideContext.Provider>
  );
}

function CanvasRendererContent({
  elements,
  config,
  mode,
  respectSavedVisibility = false,
  fieldState = {},
  renderEmbeddedCanvas,
  renderViewGrid,
  renderTimeline,
  renderDataWidget,
  title,
  description,
  footer,
  responsive = false,
  forcedBreakpoint,
}: CanvasRendererProps) {
  const { columns, rowHeight, gap } = config;
  const inheritedAutoHeight = useContext(CanvasAutoHeightContext);

  // Root-level elements (those without a parent) are the scope the responsive
  // mapping operates on.
  const rootElements = useMemo(
    () => elements.filter((el) => !el.parentElementId),
    [elements],
  );

  // Live viewport breakpoint. Inert (always desktop) when responsive mapping is
  // off, so a non-responsive caller is byte-identical to before this change.
  const detectedBreakpoint = useCanvasBreakpoint(responsive);

  // Passing forcedBreakpoint implies responsive mapping is active even when the
  // `responsive` prop is false (the builder preview forces a breakpoint without
  // opting into the live-viewport path). Otherwise the live breakpoint drives.
  const active = responsive || forcedBreakpoint != null;
  const breakpoint: BreakpointKind = forcedBreakpoint ?? detectedBreakpoint;

  // Effective root column count for this breakpoint (spec 7.2): desktop keeps
  // the authored count, tablet is 8, phone is 1.
  const rootColumns = active ? effectiveColumns(breakpoint, columns) : columns;

  // Breakpoint-mapped geometry for EVERY element (root and nested), respecting
  // TABS panel scoping. Null when mapping is inactive, in which case authored
  // positions are used throughout and rendering is byte-identical to before.
  // Desktop keeps the authored rows and columns (no shelf-packing), so a tall
  // side card does not push the elements authored beside it below its last row.
  // Tablet and phone still pack (see `mapScopeWithColumns`).
  const geometry = useMemo(
    () => buildGeometryMap(elements, breakpoint, rootColumns, columns, active),
    [elements, breakpoint, rootColumns, columns, active],
  );

  // Root-level elements repositioned for the current breakpoint. Each entry is a
  // shallow clone of the authored element with its grid position/size replaced
  // by the mapped values; the element id is preserved so GridElement still finds
  // its children in the untouched `elements` array. When mapping is inactive (or
  // on desktop, where the map is the identity) these equal the authored
  // positions.
  const positionedRoots = useMemo(() => {
    if (!geometry) {
      return rootElements;
    }
    return rootElements.map((el) => {
      const m = geometry.get(el.id);
      return m
        ? {
            ...el,
            gridColumn: m.col,
            gridRow: m.row,
            columnSpan: m.w,
            rowSpan: m.h,
          }
        : el;
    });
  }, [geometry, rootElements]);

  // Calculate the maximum row to determine grid height. Use the positioned root
  // set when mapping is active (phone stacking grows the row count); otherwise
  // keep the original all-elements calculation so the inactive path is unchanged.
  const maxRow = (active ? positionedRoots : elements).reduce(
    (max, el) => Math.max(max, el.gridRow + el.rowSpan - 1),
    0,
  );
  const totalRows = Math.max(maxRow + 1, config.minRows);

  // Opt-in content-driven height: when any root element sets `auto_height: true`, the root
  // rows size to their content and every other element keeps its authored height by a
  // minimum, so a canvas without the key renders exactly as before.
  const hasAutoHeight =
    inheritedAutoHeight ||
    positionedRoots.some((el) => el.props?.auto_height === true);
  const contentRoots = hasAutoHeight
    ? compactContentRows(positionedRoots)
    : positionedRoots;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Title and description */}
      {title && (
        <Typography level="h4" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography level="body-sm" sx={{ mb: 3, color: "text.secondary" }}>
          {description}
        </Typography>
      )}

      {/* Grid-based canvas area.
          On the responsive / forced-breakpoint path (`active`), row height, gap,
          and the minimum height are scaled by the canvas density factor
          (spec 14.2) via `--canvas-density-scale`, which the CanvasThemeScope
          wrapper sets. The two runtime hosts that pass responsive (CanvasApp,
          PublicCanvasViewer) are exactly the CanvasThemeScope wrap sites, so the
          density variable is only ever present on the active path. On the
          inactive path there is no wrapper and no variable, so we emit the exact
          pre-responsive style values -- the identical JavaScript values the base
          used, string bytes included -- keeping a responsive-off canvas
          byte-identical to before this change. */}
      <Box
        data-testid="canvas-root-grid"
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: `repeat(${rootColumns}, 1fr)`,
          gridAutoRows: hasAutoHeight
            ? "minmax(0, auto)"
            : active
              ? `calc(${rowHeight}px * var(--canvas-density-scale, 1))`
              : rowHeight,
          gap: active
            ? `calc(${gap}px * var(--canvas-density-scale, 1))`
            : `${gap}px`,
          minHeight: hasAutoHeight
            ? undefined
            : active
              ? `calc(${totalRows * (rowHeight + gap)}px * var(--canvas-density-scale, 1))`
              : totalRows * (rowHeight + gap),
        }}
      >
        {elements.length === 0 ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              gridRow: "1 / span 3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              {mode === "edit"
                ? "No elements to preview"
                : "This canvas has no elements configured."}
            </Typography>
          </Box>
        ) : (
          // Only render root-level elements (those without a parent). When
          // responsive, each carries its breakpoint-mapped position; children
          // are still resolved from the untouched `elements` array by id.
          positionedRoots.map((element, index) => (
            <GridElement
              key={element.id}
              element={element}
              contentGeometry={hasAutoHeight ? contentRoots[index] : undefined}
              allElements={elements}
              mode={mode}
              respectSavedVisibility={respectSavedVisibility}
              fieldState={fieldState}
              renderEmbeddedCanvas={renderEmbeddedCanvas}
              renderViewGrid={renderViewGrid}
              renderTimeline={renderTimeline}
              renderDataWidget={renderDataWidget}
              geometry={geometry}
              rowMinHeight={
                hasAutoHeight &&
                !(
                  element.props?.auto_height === true ||
                  (inheritedAutoHeight && element.props?.auto_height !== false)
                )
                  ? active
                    ? `calc(${element.rowSpan * rowHeight + (element.rowSpan - 1) * gap}px * var(--canvas-density-scale, 1))`
                    : element.rowSpan * rowHeight + (element.rowSpan - 1) * gap
                  : undefined
              }
              autoHeight={
                element.props?.auto_height === true ||
                (inheritedAutoHeight && element.props?.auto_height !== false)
              }
            />
          ))
        )}
      </Box>

      {/* Footer (e.g., submit button) */}
      {footer}
    </Box>
  );
}

/**
 * GridElement - Renders a single element positioned on the grid.
 */
interface GridElementProps {
  element: RenderableElement;
  allElements: RenderableElement[];
  mode: "edit" | "runtime";
  respectSavedVisibility: boolean;
  fieldState: Record<string, FieldState>;
  renderEmbeddedCanvas?: (element: RenderableElement) => React.ReactNode;
  renderViewGrid?: (element: RenderableElement) => React.ReactNode;
  renderTimeline?: (element: RenderableElement) => React.ReactNode;
  /** Draws KANBAN and CALENDAR widgets (they need the canvas data sources and write context). */
  renderDataWidget?: (element: RenderableElement) => React.ReactNode;
  /**
   * Breakpoint-mapped geometry for every element, or null when responsive
   * mapping is inactive. Threaded down so nested container scopes are placed at
   * their mapped positions and sized to their effective child columns.
   */
  geometry: Map<string, MappedItem> | null;
  /** On a canvas with an auto_height element, the authored height this element keeps. */
  rowMinHeight?: number | string;
  /** The element sizes to its content (auto_height: true). */
  autoHeight?: boolean;
  contentGeometry?: { gridRow: number; rowSpan: number };
}

function isElementVisible(
  element: RenderableElement,
  mode: "edit" | "runtime",
  fieldState: Record<string, FieldState>,
  respectSavedVisibility = false,
): boolean {
  if (mode === "edit") {
    return !respectSavedVisibility || element.isVisible !== false;
  }
  if (element.isVisible === false) return false;
  const formData: Record<string, unknown> = {};
  Object.entries(fieldState).forEach(([key, state]) => {
    formData[key] = state.value;
  });
  return evaluateVisibilityRules(element.visibilityRules, formData);
}

function GridElement({
  element,
  allElements,
  mode,
  respectSavedVisibility,
  fieldState,
  renderEmbeddedCanvas,
  renderViewGrid,
  renderTimeline,
  renderDataWidget,
  geometry,
  rowMinHeight,
  autoHeight,
  contentGeometry,
}: GridElementProps) {
  const { gridColumn, gridRow, columnSpan, rowSpan } = element;

  // Get the field state for this element if it has a data binding
  const elementFieldState = element.data_binding?.field_path
    ? fieldState[element.data_binding.field_path]
    : undefined;

  const isVisible = isElementVisible(
    element,
    mode,
    fieldState,
    respectSavedVisibility,
  );

  // If not visible, don't render anything
  if (!isVisible) {
    return null;
  }

  return (
    <Box
      data-testid={`canvas-element-${element.id}`}
      sx={{
        gridColumn: `${gridColumn} / span ${columnSpan}`,
        gridRow: `${contentGeometry?.gridRow ?? gridRow} / span ${contentGeometry?.rowSpan ?? rowSpan}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: autoHeight ? "flex-start" : "center",
        ...(autoHeight ? { alignSelf: "start" } : {}),
        ...(rowMinHeight !== undefined ? { minHeight: rowMinHeight } : {}),
        minWidth: 0, // Allow shrinking in grid
      }}
    >
      <CanvasAutoHeightContext.Provider value={autoHeight === true}>
        <ElementContent
          element={element}
          allElements={allElements}
          mode={mode}
          respectSavedVisibility={respectSavedVisibility}
          fieldState={elementFieldState}
          allFieldState={fieldState}
          renderEmbeddedCanvas={renderEmbeddedCanvas}
          renderViewGrid={renderViewGrid}
          renderTimeline={renderTimeline}
          renderDataWidget={renderDataWidget}
          geometry={geometry}
        />
      </CanvasAutoHeightContext.Provider>
    </Box>
  );
}

/**
 * ElementContent - Renders the actual content of an element.
 */
interface ElementContentProps {
  element: RenderableElement;
  allElements: RenderableElement[];
  mode: "edit" | "runtime";
  respectSavedVisibility: boolean;
  fieldState?: FieldState;
  allFieldState: Record<string, FieldState>;
  renderEmbeddedCanvas?: (element: RenderableElement) => React.ReactNode;
  renderViewGrid?: (element: RenderableElement) => React.ReactNode;
  renderTimeline?: (element: RenderableElement) => React.ReactNode;
  /** Draws KANBAN and CALENDAR widgets (they need the canvas data sources and write context). */
  renderDataWidget?: (element: RenderableElement) => React.ReactNode;
  /** Breakpoint-mapped geometry, or null when responsive mapping is inactive. */
  geometry: Map<string, MappedItem> | null;
}

function ElementContent(contentProps: ElementContentProps) {
  const { element } = contentProps;
  const override = useContext(ElementOverrideContext)?.(element);
  if (override !== undefined) return <>{override}</>;
  const styleSx = resolveStyleOverrides(element.styleOverrides);
  if (!styleSx) return <ElementContentBody {...contentProps} />;
  // CARD and SECTION containers apply the overrides to their own surface; every
  // other element is wrapped so the stored overrides are not silently ignored.
  const containerType =
    (element.props as { containerType?: string }).containerType || "SECTION";
  const ownsStyle =
    (element.type === "container" ||
      element.type === "card" ||
      element.type === "tabs") &&
    (containerType === "CARD" || containerType === "SECTION");
  if (ownsStyle) {
    return <ElementContentBody {...contentProps} styleSx={styleSx} />;
  }
  return (
    <Box
      sx={{
        // Fill the grid cell like the unwrapped element does.
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        ...(styleSx.borderColor ? { border: "1px solid" } : {}),
        ...styleSx,
        borderRadius: "var(--canvas-radius, var(--joy-radius-md))",
        p: 1,
      }}
    >
      <ElementContentBody {...contentProps} />
    </Box>
  );
}

function ElementContentBody({
  element,
  allElements,
  mode,
  respectSavedVisibility,
  fieldState,
  allFieldState,
  renderEmbeddedCanvas,
  renderViewGrid,
  renderTimeline,
  renderDataWidget,
  geometry,
  styleSx,
}: ElementContentProps & { styleSx?: ResolvedStyleOverrides }) {
  const { type, variant, props } = element;
  const isEditMode = mode === "edit";
  const runtime = useCanvasRuntime();
  const autoHeight = useContext(CanvasAutoHeightContext);

  // In edit mode, show disabled preview
  // In runtime mode, show interactive form fields
  switch (type) {
    case "field":
      if (
        !isEditMode &&
        (variant || props.variant || props.inputType) === "file"
      ) {
        return (
          <ViewGridErrorBoundary
            fallback={
              <Typography color="danger">
                Unable to display this file field.
              </Typography>
            }
          >
            <CanvasFileField
              element={element}
              fieldState={fieldState}
              readonly={
                element.effectiveAccess === "read" ||
                !!props.readonly ||
                !!props.disabled
              }
            />
          </ViewGridErrorBoundary>
        );
      }
      return (
        <FieldElement
          variant={variant || (props.variant as string) || "text-input"}
          props={
            element.effectiveAccess === "read"
              ? { ...props, readonly: true }
              : props
          }
          isPreview={isEditMode}
          fieldState={fieldState}
          rowData={
            runtime?.rowData ??
            Object.fromEntries(
              Object.entries(allFieldState).map(([key, state]) => [
                key,
                state.value,
              ]),
            )
          }
        />
      );

    case "container":
    case "card":
    case "tabs": {
      const selectionBinding = (
        props as { selection_binding?: { empty_state_text?: string } }
      ).selection_binding;
      if (mode === "runtime" && selectionBinding) {
        const detail = runtime?.selectionDetail;
        if (!detail?.rowId) {
          return (
            <Typography level="body-sm">
              {selectionBinding.empty_state_text ||
                "Select a record to view details."}
            </Typography>
          );
        }
        if (detail.loading)
          return (
            <Typography level="body-sm">Loading selected record...</Typography>
          );
        if (detail.error)
          return (
            <Typography level="body-sm" color="danger">
              Selected record unavailable.
            </Typography>
          );
      }
      const containerType =
        (props as unknown as { containerType?: string }).containerType ||
        "SECTION";
      const title =
        (props as unknown as { title?: string }).title || props.label;
      const panels = (
        props as unknown as {
          panels?: Array<{ id: string; label: string; icon?: string }>;
        }
      ).panels;
      const containerProps = props as unknown as {
        defaultPanelId?: string;
        default_panel_id?: string;
        tabStyle?: string;
      };
      const defaultPanelId =
        containerProps.defaultPanelId ?? containerProps.default_panel_id;

      // Get children of this container
      const containerChildren = allElements.filter(
        (el) => el.parentElementId === element.id,
      );

      // Render children within a container grid
      const renderContainerChildren = (panelId?: string) => {
        const children = containerChildren.filter(
          (child) =>
            (!panelId || child.parentPanelId === panelId) &&
            isElementVisible(
              child,
              mode,
              allFieldState,
              respectSavedVisibility,
            ),
        );

        if (children.length === 0) {
          return (
            <Typography
              level="body-sm"
              sx={{ color: "text.tertiary", textAlign: "center", py: 2 }}
            >
              {mode === "edit" ? "Drop elements here" : "No content"}
            </Typography>
          );
        }

        // Nested grid column count. When responsive mapping is active, the
        // engine sized this container's child scope to its effective child
        // columns (spec 7.2, carried on the container's mapped geometry); use
        // that so the nest re-packs at tablet/phone. When inactive, keep the
        // authored 12-column nest so rendering is byte-identical to before.
        const containerGeom = geometry?.get(element.id);
        const nestedColumns = containerGeom?.childColumns ?? 12;
        const positionedChildren = children.map((child) => ({
          ...child,
          gridRow: geometry?.get(child.id)?.row ?? child.gridRow,
          rowSpan: geometry?.get(child.id)?.h ?? (child.rowSpan || 1),
        }));
        const contentChildren = autoHeight
          ? compactContentRows(positionedChildren)
          : positionedChildren;

        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${nestedColumns}, 1fr)`,
              // Row height and gap scale with the canvas density factor on the
              // active path, matching the root grid. `geometry` is non-null
              // exactly when responsive mapping is active (buildGeometryMap
              // returns null otherwise), and the density variable is only ever
              // set on that path. On the inactive path emit the exact
              // pre-responsive literals so the nest is byte-identical to before.
              gridAutoRows: autoHeight
                ? "minmax(0, auto)"
                : geometry
                  ? "minmax(calc(60px * var(--canvas-density-scale, 1)), auto)"
                  : "minmax(60px, auto)",
              gap: geometry
                ? "calc(8px * var(--canvas-density-scale, 1))"
                : "8px",
            }}
          >
            {children.map((child, index) => {
              // A child's placement comes from its mapped geometry when active,
              // else from its authored position (unchanged legacy behaviour).
              const childGeom = geometry?.get(child.id);
              const col = childGeom?.col ?? child.gridColumn;
              const span = childGeom?.w ?? (child.columnSpan || 12);
              const row = contentChildren[index].gridRow;
              const rspan = contentChildren[index].rowSpan;
              const childAutoHeight =
                child.props?.auto_height === true ||
                (autoHeight && child.props?.auto_height !== false);
              return (
                <Box
                  key={child.id}
                  data-testid={`canvas-element-${child.id}`}
                  sx={{
                    gridColumn: `${col} / span ${span}`,
                    gridRow: `${row} / span ${rspan}`,
                    minWidth: 0,
                    ...(autoHeight && !childAutoHeight
                      ? {
                          minHeight:
                            positionedChildren[index].rowSpan * 60 +
                            (positionedChildren[index].rowSpan - 1) * 8,
                        }
                      : {}),
                  }}
                >
                  <CanvasAutoHeightContext.Provider value={childAutoHeight}>
                    <ElementContent
                      element={child}
                      allElements={allElements}
                      mode={mode}
                      respectSavedVisibility={respectSavedVisibility}
                      fieldState={
                        child.data_binding?.field_path
                          ? allFieldState[child.data_binding.field_path]
                          : undefined
                      }
                      allFieldState={allFieldState}
                      renderEmbeddedCanvas={renderEmbeddedCanvas}
                      renderViewGrid={renderViewGrid}
                      renderTimeline={renderTimeline}
                      renderDataWidget={renderDataWidget}
                      geometry={geometry}
                    />
                  </CanvasAutoHeightContext.Provider>
                </Box>
              );
            })}
          </Box>
        );
      };

      // TABS container
      if (containerType === "TABS" && panels && panels.length > 0) {
        return (
          <TabsContainer
            containerId={element.id}
            title={title}
            panels={panels}
            defaultPanelId={defaultPanelId}
            tabStyle={containerProps.tabStyle}
            renderChildren={renderContainerChildren}
          />
        );
      }

      // CARD container
      if (containerType === "CARD") {
        return (
          <Card
            variant="outlined"
            sx={{
              height: autoHeight ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              // Container chrome consumes the canvas radius token (spec 14.2).
              // Unset -> the same Joy md radius the Card used before.
              borderRadius: "var(--canvas-radius, var(--joy-radius-md))",
              ...styleSx,
            }}
          >
            {title && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography level="title-sm" fontWeight="lg">
                  {title}
                </Typography>
              </Box>
            )}
            <Box sx={{ flexGrow: 1, p: 2 }}>{renderContainerChildren()}</Box>
          </Card>
        );
      }

      // Default SECTION container
      return (
        <Box
          sx={{
            height: autoHeight ? "auto" : "100%",
            display: "flex",
            flexDirection: "column",
            // Container chrome consumes the canvas radius token (spec 14.2).
            // Unset -> the same Joy md radius this section used before.
            borderRadius: "var(--canvas-radius, var(--joy-radius-md))",
            ...(styleSx?.borderColor ? { border: "1px solid" } : {}),
            ...styleSx,
          }}
        >
          {title && (
            <Typography
              level="title-sm"
              fontWeight="lg"
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {title}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1, p: title ? 2 : 1 }}>
            {renderContainerChildren()}
          </Box>
        </Box>
      );
    }

    case "text":
    case "static":
      if (
        mode === "runtime" &&
        element.data_binding?.field_path &&
        fieldState
      ) {
        const statusColors = sanitizeStatusColors(props.statusColors);
        if (statusColors) {
          return (
            <StatusChip
              value={String(fieldState.value ?? "")}
              statusColors={statusColors}
            />
          );
        }
        if ((props as { avatar?: unknown }).avatar === true) {
          return <InitialsAvatar name={String(fieldState.value ?? "")} />;
        }
        return (
          <Typography level="body-sm">
            {String(fieldState.value ?? "")}
          </Typography>
        );
      }
      return renderTextElement(props);

    case "divider":
      return <Divider />;

    case "action_button":
      return (
        <ActionButtonElement
          props={props}
          isPreview={mode === "edit"}
          readOnly={element.effectiveAccess === "read"}
          elementId={element.id}
        />
      );

    case "embedded_canvas": {
      if (mode === "runtime" && renderEmbeddedCanvas) {
        return <>{renderEmbeddedCanvas(element)}</>;
      }

      // Get embedding config from props
      const childCanvasId = (
        props as unknown as { childCanvasId?: string | number }
      ).childCanvasId;
      const childFilterField = (
        props as unknown as { childFilterField?: string }
      ).childFilterField;
      const parentKeyField = (props as unknown as { parentKeyField?: string })
        .parentKeyField;

      // In edit mode, show a preview placeholder
      // In runtime mode, the EmbeddedCanvasRenderer would be used instead (see CanvasApp)
      if (!childCanvasId) {
        return (
          <Sheet
            variant="soft"
            color="warning"
            sx={{
              height: "100%",
              minHeight: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "md",
              border: "2px dashed",
              borderColor: "warning.300",
              p: 2,
              gap: 1,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 32, color: "warning.500" }} />
            <Typography
              level="body-sm"
              sx={{ color: "warning.700", fontWeight: "md" }}
            >
              Embedded Canvas
            </Typography>
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", textAlign: "center" }}
            >
              Not configured
            </Typography>
          </Sheet>
        );
      }

      return (
        <Sheet
          variant="outlined"
          sx={{
            height: "100%",
            minHeight: 120,
            borderRadius: "md",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              bgcolor: "background.level1",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <DashboardIcon sx={{ fontSize: 18, color: "primary.500" }} />
            <Typography level="body-sm" fontWeight="md">
              {props.label || "Embedded Canvas"}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              bgcolor: "background.level1",
            }}
          >
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", textAlign: "center" }}
            >
              {parentKeyField && childFilterField
                ? `Linked: ${parentKeyField} → ${childFilterField}`
                : `Canvas ID: ${childCanvasId}`}
            </Typography>
          </Box>
        </Sheet>
      );
    }

    case "widget":
      return <CanvasAggregateWidget element={element} mode={mode} />;

    case "hierarchy": {
      if (mode === "runtime" && renderDataWidget)
        return <>{renderDataWidget(element)}</>;
      return (
        <Sheet variant="outlined" sx={{ p: 2 }}>
          <Typography level="title-sm">
            {String(props.title || "Hierarchy")}
          </Typography>
          <Typography level="body-sm">
            Visible items connected by existing relationships
          </Typography>
        </Sheet>
      );
    }

    case "related_records":
    case "kanban":
    case "calendar": {
      const isBoard = element.type === "kanban";
      const isRelated = element.type === "related_records";
      if (mode === "runtime" && renderDataWidget)
        return <>{renderDataWidget(element)}</>;
      return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm" }}>
          <Typography level="title-sm">
            {String(
              props.title ||
                (isRelated
                  ? "Related records"
                  : isBoard
                    ? "Kanban board"
                    : "Calendar"),
            )}
          </Typography>
          <Typography level="body-sm">
            {mode === "runtime"
              ? `${isRelated ? "Related records" : isBoard ? "Boards" : "Calendars"} require an authenticated canvas.`
              : `${isRelated ? "Related records" : isBoard ? "Board" : "Calendar"} from ${String(props.data_source_alias || "main")}`}
          </Typography>
        </Sheet>
      );
    }

    case "timeline": {
      if (mode === "runtime" && renderTimeline)
        return <>{renderTimeline(element)}</>;
      return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm" }}>
          <Typography level="title-sm">
            {String(props.title || "Timeline")}
          </Typography>
          <Typography level="body-sm">
            {mode === "runtime"
              ? "Timelines require an authenticated canvas."
              : `Timeline from ${String(props.data_source_alias || "main")}`}
          </Typography>
        </Sheet>
      );
    }

    case "view_grid": {
      if (mode === "runtime" && renderViewGrid) {
        return <>{renderViewGrid(element)}</>;
      }

      const dataSourceAlias = (
        props as unknown as { data_source_alias?: string }
      ).data_source_alias;
      const title = (props as unknown as { title?: string }).title;

      return (
        <CanvasViewGridPlaceholder
          title={title || (props.label as string | undefined)}
          message={
            mode === "runtime"
              ? "View grids require an authenticated canvas."
              : `Browse records from ${dataSourceAlias || "main"}`
          }
        />
      );
    }

    default:
      return (
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Unknown element: {type}
        </Typography>
      );
  }
}

/**
 * FieldElement - Renders form field elements.
 */
interface FieldElementProps {
  variant: string;
  props: CanvasElementProps;
  isPreview: boolean;
  fieldState?: FieldState;
  rowData?: Record<string, unknown>;
}

/** The text shown for a field value in `displayMode: "text"`. */
function displayValueOf(
  variant: string,
  value: unknown,
  options?: Array<{ value: string | number | boolean; label: string }>,
): string {
  if (value === undefined || value === null || value === "") return "—";
  if (variant === "boolean" || variant === "checkbox") {
    return value ? "Yes" : "No";
  }
  const match = options?.find((opt) => String(opt.value) === String(value));
  return match ? match.label : String(value);
}

/** Canvas FIELD options are stored as strings by the agent and as labeled
 * objects by the builder. Normalize both shapes for the shared runtime control. */
type NormalizedSelectOption = {
  value: string | number | boolean;
  label: string;
};

function normalizeSelectOptions(options: unknown): NormalizedSelectOption[] {
  if (!Array.isArray(options)) return [];

  return options.flatMap((option): NormalizedSelectOption[] => {
    if (
      typeof option === "string" ||
      typeof option === "number" ||
      typeof option === "boolean"
    ) {
      return [{ value: option, label: String(option) }];
    }

    if (
      option &&
      typeof option === "object" &&
      "value" in option &&
      (typeof option.value === "string" ||
        typeof option.value === "number" ||
        typeof option.value === "boolean")
    ) {
      return [
        {
          value: option.value,
          label:
            "label" in option && option.label != null
              ? String(option.label)
              : String(option.value),
        },
      ];
    }

    return [];
  });
}

function FieldElement({
  variant,
  props,
  isPreview,
  fieldState,
  rowData = {},
}: FieldElementProps) {
  const { value, error, touched, onChange, onBlur } = fieldState || {};
  const selectOptions = normalizeSelectOptions(props.options);
  const showError = touched && !!error;
  const isDisabled = isPreview || props.disabled || props.readonly;
  const isReadOnly = isPreview || Boolean(props.readonly);

  const renderLabel = () => (
    <FormLabel
      sx={{ mb: 0.5, fontSize: "sm", fontWeight: "md", lineHeight: 1.4 }}
    >
      {props.label}
      {props.required && (
        <Typography component="span" sx={{ color: "danger.500" }}>
          {" "}
          *
        </Typography>
      )}
    </FormLabel>
  );

  const renderHelper = () => {
    if (showError) {
      return <FormHelperText sx={{ mt: 0.5 }}>{error}</FormHelperText>;
    }
    if (props.helperText) {
      return (
        <FormHelperText sx={{ mt: 0.5 }}>{props.helperText}</FormHelperText>
      );
    }
    return null;
  };

  // Read-only display: the label and the value as text, not a greyed-out control.
  if (props.displayMode === "text") {
    return (
      <FormControl>
        {renderLabel()}
        <Typography
          level="body-md"
          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
        >
          {typeof props.record_label_template === "string" &&
          props.record_label_template.trim()
            ? formatRecordLabelTemplate(props.record_label_template, rowData)
            : displayValueOf(variant, value, selectOptions)}
        </Typography>
      </FormControl>
    );
  }

  switch (variant) {
    case "radio":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <RadioGroup
            size="sm"
            orientation={
              props.orientation === "vertical" ? "vertical" : "horizontal"
            }
            value={value != null ? String(value) : ""}
            onChange={(event) => onChange?.(event.target.value)}
            onBlur={onBlur}
          >
            {props.options?.map((opt) => (
              <Radio
                key={String(opt.value)}
                value={String(opt.value)}
                label={opt.label}
                disabled={isDisabled}
              />
            ))}
          </RadioGroup>
          {renderHelper()}
        </FormControl>
      );

    case "text":
    case "text-input":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            value={(value as string) || ""}
            placeholder={props.placeholder || "Enter text..."}
            disabled={Boolean(props.disabled)}
            slotProps={{
              input: {
                readOnly: isReadOnly && !props.disabled,
                "aria-readonly": isReadOnly && !props.disabled,
                disabled: Boolean(props.disabled),
              },
            }}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "number":
    case "number-input":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            type="number"
            value={value !== undefined && value !== null ? String(value) : ""}
            placeholder={props.placeholder || "0"}
            disabled={isDisabled}
            onChange={(e) => {
              const numValue = e.target.value ? Number(e.target.value) : null;
              onChange?.(numValue);
            }}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "textarea":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Textarea
            size="sm"
            value={(value as string) || ""}
            placeholder={props.placeholder}
            disabled={Boolean(props.disabled)}
            slotProps={{
              textarea: {
                readOnly: isReadOnly && !props.disabled,
                "aria-readonly": isReadOnly && !props.disabled,
                disabled: Boolean(props.disabled),
              },
            }}
            minRows={props.rows || 3}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "boolean":
    case "checkbox":
      return (
        <FormControl error={showError}>
          <Checkbox
            size="sm"
            label={
              <span>
                {props.label}
                {props.required && (
                  <Typography component="span" sx={{ color: "danger.500" }}>
                    {" "}
                    *
                  </Typography>
                )}
              </span>
            }
            checked={!!value}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.checked)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "select":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Select
            size="sm"
            value={value != null ? String(value) : null}
            placeholder={props.placeholder || "Select..."}
            disabled={isDisabled}
            onChange={(_, newValue) => onChange?.(newValue)}
            onClose={onBlur}
          >
            {selectOptions.map((opt) => (
              <Option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </Option>
            ))}
            {selectOptions.length === 0 && (
              <Option value="" disabled>
                No options
              </Option>
            )}
          </Select>
          {renderHelper()}
        </FormControl>
      );

    case "date":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            type="date"
            value={value ? String(value).split("T")[0] : ""}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "datetime":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            type="datetime-local"
            value={value ? String(value).slice(0, 16) : ""}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "section":
      return (
        <Box
          sx={{
            p: 2,
            borderRadius: "sm",
            bgcolor: "background.level1",
            textAlign: "center",
            minHeight: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            Section container
          </Typography>
        </Box>
      );

    case "label":
      return (
        <Typography
          level={
            props.size === "lg"
              ? "h4"
              : props.size === "sm"
                ? "body-sm"
                : "body-md"
          }
        >
          {props.label || "Label"}
        </Typography>
      );

    default:
      // Fallback to text input
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            value={(value as string) || ""}
            placeholder={props.placeholder}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );
  }
}

export default CanvasRenderer;
